import { utils, writeFile } from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ExportControl {
  _id: string;
  identifier: string;
  description: string;
  category?: string;
  severity?: string;
  findings?: any[];
}

interface ExportFinding {
  evidence?: string;
  status: string;
  priority: string;
  currentState: string;
  remediationPlan?: string;
}

/**
 * Export gap controls to Excel format
 */
export const exportToExcel = (controls: ExportControl[], filename = 'gap-analysis-report') => {
  // Prepare summary data
  const summaryData = controls.map(control => ({
    'Control ID': control.identifier,
    'Description': control.description,
    'Category': control.category || 'N/A',
    'Severity': control.severity || 'N/A',
    'Findings Count': control.findings?.length || 0,
    'Status': (control.findings && control.findings.length > 0) ? 'Non-Compliant' : 'Compliant'
  }));

  // Prepare detailed findings data
  const findingsData: any[] = [];
  controls.forEach(control => {
    if (control.findings && control.findings.length > 0) {
      control.findings.forEach((finding: ExportFinding) => {
        findingsData.push({
          'Control ID': control.identifier,
          'Control Description': control.description,
          'Evidence': finding.evidence || 'N/A',
          'Current State': finding.currentState,
          'Status': finding.status,
          'Priority': finding.priority,
          'Remediation Plan': finding.remediationPlan || 'N/A'
        });
      });
    }
  });

  // Create workbook
  const wb = utils.book_new();

  // Add summary sheet
  const wsSummary = utils.json_to_sheet(summaryData);
  utils.book_append_sheet(wb, wsSummary, 'Controls Summary');

  // Add findings sheet if there are findings
  if (findingsData.length > 0) {
    const wsFindings = utils.json_to_sheet(findingsData);
    utils.book_append_sheet(wb, wsFindings, 'Detailed Findings');
  }

  // Add statistics sheet
  const stats = {
    'Total Controls': controls.length,
    'Compliant Controls': controls.filter(c => !c.findings || c.findings.length === 0).length,
    'Non-Compliant Controls': controls.filter(c => c.findings && c.findings.length > 0).length,
    'Total Findings': findingsData.length,
    'Critical Findings': findingsData.filter((f: any) => f.Priority === 'Critical').length,
    'High Priority Findings': findingsData.filter((f: any) => f.Priority === 'High').length,
    'Open Findings': findingsData.filter((f: any) => f.Status === 'Open').length
  };

  const wsStats = utils.json_to_sheet([stats]);
  utils.book_append_sheet(wb, wsStats, 'Statistics');

  // Write file
  writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export gap controls to PDF format
 */
export const exportToPDF = (controls: ExportControl[], filename = 'gap-analysis-report') => {
  const doc = new jsPDF();
  
  // USCIS Blue color
  const uscisBlue: [number, number, number] = [0, 51, 102];
  
  // Title
  doc.setFontSize(18);
  doc.setTextColor(uscisBlue[0], uscisBlue[1], uscisBlue[2]);
  doc.text('Gap Analysis Report', 14, 20);
  
  // Metadata
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
  doc.text(`Total Controls: ${controls.length}`, 14, 34);
  
  // Summary Statistics
  const compliantCount = controls.filter(c => !c.findings || c.findings.length === 0).length;
  const nonCompliantCount = controls.filter(c => c.findings && c.findings.length > 0).length;
  const totalFindings = controls.reduce((sum, c) => sum + (c.findings?.length || 0), 0);
  
  doc.setFontSize(12);
  doc.setTextColor(uscisBlue[0], uscisBlue[1], uscisBlue[2]);
  doc.text('Executive Summary', 14, 45);
  
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(`Compliant Controls: ${compliantCount}`, 20, 52);
  doc.text(`Non-Compliant Controls: ${nonCompliantCount}`, 20, 58);
  doc.text(`Total Findings: ${totalFindings}`, 20, 64);
  
  // Controls Table
  doc.setFontSize(12);
  doc.setTextColor(uscisBlue[0], uscisBlue[1], uscisBlue[2]);
  doc.text('Gap Controls', 14, 75);
  
  autoTable(doc, {
    head: [['Control ID', 'Description', 'Category', 'Severity', 'Findings', 'Status']],
    body: controls.map(control => [
      control.identifier,
      control.description.length > 40 ? control.description.substring(0, 37) + '...' : control.description,
      control.category || 'N/A',
      control.severity || 'N/A',
      (control.findings?.length || 0).toString(),
      (control.findings && control.findings.length > 0) ? 'Non-Compliant' : 'Compliant'
    ]),
    startY: 80,
    headStyles: { 
      fillColor: [uscisBlue[0], uscisBlue[1], uscisBlue[2]],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold'
    },
    styles: { 
      fontSize: 8,
      cellPadding: 2
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      1: { cellWidth: 60 } // Description column wider
    }
  });
  
  // Findings Details (if any)
  const findingsData: any[] = [];
  controls.forEach(control => {
    if (control.findings && control.findings.length > 0) {
      control.findings.forEach((finding: ExportFinding) => {
        findingsData.push([
          control.identifier,
          finding.evidence?.substring(0, 50) || 'N/A',
          finding.currentState,
          finding.status,
          finding.priority
        ]);
      });
    }
  });
  
  if (findingsData.length > 0) {
    doc.addPage();
    doc.setFontSize(12);
    doc.setTextColor(uscisBlue[0], uscisBlue[1], uscisBlue[2]);
    doc.text('Detailed Findings', 14, 20);
    
    autoTable(doc, {
      head: [['Control', 'Evidence', 'State', 'Status', 'Priority']],
      body: findingsData,
      startY: 25,
      headStyles: { 
        fillColor: [uscisBlue[0], uscisBlue[1], uscisBlue[2]],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold'
      },
      styles: { 
        fontSize: 8,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    });
  }
  
  // Footer on all pages
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  // Save PDF
  doc.save(`${filename}.pdf`);
};

/**
 * Export filtered controls based on current filters
 */
export const exportFilteredData = (
  controls: ExportControl[],
  filters: { status: string; priority: string; category: string },
  format: 'excel' | 'pdf'
) => {
  const filteredControls = controls.filter(control => {
    if (filters.status === 'Compliant' && control.findings && control.findings.length > 0) return false;
    if (filters.status === 'NonCompliant' && (!control.findings || control.findings.length === 0)) return false;
    if (filters.priority && control.severity !== filters.priority) return false;
    if (filters.category && control.category !== filters.category) return false;
    return true;
  });

  const filename = `gap-analysis-filtered-${new Date().toISOString().split('T')[0]}`;
  
  if (format === 'excel') {
    exportToExcel(filteredControls, filename);
  } else {
    exportToPDF(filteredControls, filename);
  }
};
